import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { ProcessorDetails, useUpdateProcessor } from '@/lib/api/processors'
import { getIntegrationImage } from '@/lib/utils/integration-utils'
import toast from 'react-hot-toast'

interface ProcessorOverviewCardProps {
    processor: ProcessorDetails
    testModeWarningKey?: string
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}

const getTestMode = (options: Record<string, unknown> | null) => {
    return options?.testMode === true
}

const getDryMode = (options: Record<string, unknown> | null) => {
    return options?.dryMode === true
}

export const ProcessorOverviewCard = ({ processor, testModeWarningKey = 'testModeWarning' }: ProcessorOverviewCardProps) => {
    const { t } = useTranslation('processors')
    const isTestMode = getTestMode(processor.options)
    const isDryMode = getDryMode(processor.options)
    const [dryModeLoading, setDryModeLoading] = useState(false)
    const updateProcessor = useUpdateProcessor()

    const handleDryModeToggle = async (enabled: boolean) => {
        setDryModeLoading(true)
        try {
            await updateProcessor.mutateAsync({
                processorId: processor.id,
                updatedData: {
                    name: processor.name,
                    type: processor.type,
                    enabled: processor.enabled,
                    options: {
                        ...processor.options,
                        dryMode: enabled,
                    },
                },
            })
            toast.success(enabled ? 'Dry mode enabled' : 'Dry mode disabled')
        } catch {
            toast.error('Failed to update dry mode')
        } finally {
            setDryModeLoading(false)
        }
    }

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('overview')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-4">
                        <img
                            src={getIntegrationImage(processor.type)}
                            alt={processor.type}
                            className="w-12 h-12 object-contain"
                        />
                        <div>
                            <p className="text-sm text-muted-foreground">{t('processorName')}</p>
                            <p className="font-semibold">{processor.name}</p>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('mode')}</p>
                        <Badge variant={isTestMode ? 'secondary' : 'default'}>
                            {isTestMode ? t('testMode') : t('liveMode')}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('status')}</p>
                        <Badge variant={processor.enabled ? 'default' : 'secondary'}>
                            {processor.enabled ? t('active') : t('inactive')}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>
                </div>

                {isTestMode && (
                    <div className="mt-4 rounded-lg bg-yellow-50 p-3 border border-yellow-200">
                        <p className="text-sm text-yellow-700">
                            ⚠️ {t(testModeWarningKey)}
                        </p>
                    </div>
                )}

                <div className="mt-4 flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                        <Label className="text-sm font-medium">Dry Run Mode</Label>
                        <p className="text-xs text-muted-foreground">
                            Intercepts all PSP calls — logs the raw request but never sends it. Transactions are recorded as dry.
                        </p>
                    </div>
                    <Switch
                        checked={isDryMode}
                        onCheckedChange={handleDryModeToggle}
                        disabled={dryModeLoading}
                    />
                </div>

                {isDryMode && (
                    <div className="mt-3 rounded-lg bg-orange-50 p-3 border border-orange-200">
                        <p className="text-sm text-orange-700">
                            ⚠️ Dry run mode is active — no real payments will be processed. All PSP calls are intercepted and logged.
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
